import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, NavLink } from 'react-router-dom';
import HeadingCp from '../components/HeadingCp';
import SubmitButton from '../components/SubmitButton';
import FormVarH from '../components/FormVarH';
import TechTimeEntryCp from '../components/TechTimeEntryCp';
import { UtilConvertTimeStampToDate } from '../utils/UtilConvertTimeStampToDate';
import { UtilConvertDateTodayDate } from '../utils/UtilConvertDateTodayDate';

// …imports the same

const WipScheduleTech = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const api = `${apiUrl}/wip_crud.php`;
  const navigate = useNavigate();

  // Selected job from search
  const [jobSelect, setJobSelect] = useState(null);

  // Form fields the tech can edit (ONLY these are submitted)
  const [form, setForm] = useState({
    schedule_from: '',
    schedule_to: '',
    start_time: '',
    end_time: '',
    billable: false,
    tech_notes: '',
    // additional_tech_id: '', // optional
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  // -------- Search area (unchanged) ----------
  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchTerm(v);
    if (!v) return; // keep list as is until explicit search click
    const f = jobs.filter(j =>
      (j.customer_name && j.customer_name.toLowerCase().includes(v.toLowerCase())) ||
      (j.job_number && j.job_number.toLowerCase().includes(v.toLowerCase())) ||
      (j.job_site && j.job_site.toLowerCase().includes(v.toLowerCase()))
    );
    if (f.length) setJobs(f);
  };

  const handleSearchClick = async () => {
    setLoading(true);
    try {
      const body = { status: 'search_job', data: { search: searchTerm } };
      const res = await axios.post(`${apiUrl}/tech_crud.php`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (Array.isArray(res.data)) {
        setJobs(res.data);
        setJobSelect(null);
      } else {
        setJobs([]);
        toast.error('not found ' + searchTerm, { position: 'top-center' });
      }
    } catch {
      toast.error('Oops minor issue with the data.', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  // When tech clicks a job → open the tiny WIP form with just the needed fields
  const handlePickJob = (job) => {
    setJobSelect(job);
    // reset form for clean entry
    setForm({
      schedule_from: '',
      schedule_to: '',
      start_time: '',
      end_time: '',
      billable: false,
      tech_notes: '',
    });
  };

  const onChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Submit → create row in wip_scheduler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!jobSelect?.job_number) {
      toast.error('Select a project first.', { position: 'top-center' });
      return;
    }
    if (!form.schedule_from || !form.schedule_to) {
      toast.error('Schedule dates are required.', { position: 'top-center' });
      return;
    }
    if (form.start_time && form.end_time && form.end_time <= form.start_time) {
      toast.error('End time must be after start time.', { position: 'top-center' });
      return;
    }

    const payload = {
      status: 'techRecordWipSchedule',
      data: {
        job_number: jobSelect.job_number,
        schedule_from: form.schedule_from,
        schedule_to: form.schedule_to,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        tech_notes: form.tech_notes || null,
        billable: form.billable ? 1 : 0,
      },
    };

    try {
      const res = await axios.post(api, payload, { headers: { 'Content-Type': 'application/json' } });
      if ((Array.isArray(res.data) && res.data[0] === 'success') || res.data?.success) 
      {
         navigate(0); // adjust route as needed
         toast.success('WIP schedule saved!', { position: 'top-center' });
      } else {
        toast.error(res.data?.error || 'Failed to save.', { position: 'top-center' });
      }
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else toast.error('Server error', { position: 'top-center' });
    }
  };

  return (
    <>
      <br />
      <HeadingCp label={'Find Job Site'} />
      <div className="relative rounded-md shadow-sm flex-grow">
        <input
          type="text"
          name="search"
          value={searchTerm}
          onChange={handleSearchChange}
          className="block w-full pl-7 pr-24 sm:text-sm border-gray-300 rounded-md"
          placeholder="Search for Job# | Job Site"
        />
        <button
          onClick={handleSearchClick}
          disabled={!searchTerm || loading}
          className={`absolute right-0 top-0 bottom-0 text-white px-3 py-2 rounded-r-md ${
            !searchTerm || loading ? 'bg-gray-500' : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>

      <ul role="list" className="divide-y divide-gray-100 mt-3">
        {jobs.map(job => (
          <li key={job.id} className="flex items-center justify-between gap-x-6 py-5">
            <div className="min-w-0 divide-y-2">
              <div className="flex items-start gap-x-3">
                <p className="text-sm font-semibold leading-6 text-gray-900">{job.job_site}</p>
                <p className="mt-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset">
                  {job.f_p_type}
                </p>
              </div>
              <span className="text-xs text-green-700">{job.type} | {job.job_description}</span>
              <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
                <p className="whitespace-nowrap">{job.customer_name}</p>
                <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current"><circle cx={1} cy={1} r={1} /></svg>
                <p className="whitespace-nowrap">{job.job_number}</p>
                <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current"><circle cx={1} cy={1} r={1} /></svg>
                <p className="truncate">Project Manager: {job.pm}</p>
              </div>
            </div>
            <div onClick={() => handlePickJob(job)} className="flex flex-none items-center gap-x-4 text-blue-800 cursor-pointer">
              <ChevronRightIcon className="h-5" />
            </div>
          </li>
        ))}
      </ul>

      {/* Tech WIP tiny form */}
      {jobSelect && (
        <form onSubmit={handleSubmit} className="px-6 bg-white border rounded-lg py-8 mt-4">
          <div className="space-y-8">
            <div className="border-b border-gray-900/10 pb-6">
              <h6 className="text-base font-semibold leading-7 text-gray-600">
                <span className="text-blue-500 text-sm">Job#: {jobSelect.job_number}</span>
                <br />
                <span className="text-gray-500 text-sm">{jobSelect.job_site}</span>
                <br />
                <span className="text-gray-500 text-sm">{jobSelect.customer_name}</span>
              </h6>

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                {/* Schedule From/To */}
                <div className="sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">Schedule From</label>
                  <input
                    type="date"
                    name="schedule_from"
                    value={form.schedule_from}
                    onChange={onChange}
                    className="mt-1 block w-full border rounded py-2 px-3"
                    required
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">Schedule To</label>
                  <input
                    type="date"
                    name="schedule_to"
                    value={form.schedule_to}
                    onChange={onChange}
                    className="mt-1 block w-full border rounded py-2 px-3"
                    required
                  />
                </div>

                {/* Start/End Time */}
                <div className="sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">Start Time</label>
                  <input
                    type="time"
                    name="start_time"
                    value={form.start_time}
                    onChange={onChange}
                    className="mt-1 block w-full border rounded py-2 px-3"
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">End Time</label>
                  <input
                    type="time"
                    name="end_time"
                    value={form.end_time}
                    onChange={onChange}
                    className="mt-1 block w-full border rounded py-2 px-3"
                  />
                </div>

                {/* Billable */}
                <div className="sm:col-span-3">
                  <label className="inline-flex items-center text-sm font-medium text-gray-900">
                    <input
                      type="checkbox"
                      name="billable"
                      checked={form.billable}
                      onChange={onChange}
                      className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Billable
                  </label>
                </div>

                {/* Additional Tech (optional id) */}
                {/* <div className="sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">Additional Tech ID (optional)</label>
                  <input
                    type="number"
                    name="additional_tech_id"
                    value={form.additional_tech_id}
                    onChange={onChange}
                    className="mt-1 block w-full border rounded py-2 px-3"
                    placeholder=""
                  />
                </div> */}

                {/* Tech Notes */}
                <div className="sm:col-span-6">
                  <label className="block text-sm font-medium text-gray-700">Tech Notes</label>
                  <textarea
                    name="tech_notes"
                    value={form.tech_notes}
                    onChange={onChange}
                    className="mt-1 block w-full border rounded py-2 px-3"
                    rows={4}
                    placeholder="Notes for this visit…"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-x-6">
            <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
              Submit
            </button>
          </div>
        </form>
      )}
    </>
  );
};

export default WipScheduleTech;
